// T(n), S(n), souls and the consumed bonus (docs/04 §8, §19; docs/10 §1).
import { B } from '../content/balance';
import { D, Decimal } from './decimal';
import type { Modifiers } from './modifiers';

/** Raw tribute before modifiers: T(1) = 10, then × g(n). */
export function tributeRaw(n: number): Decimal {
  let t = D(B.TRIBUTE_BASE);
  for (let k = 2; k <= n; k++) {
    const g = B.TRIBUTE_GROWTH.find((s) => k <= s.upTo)!.g;
    t = t.mul(g);
  }
  return t;
}

/** T(n) with Web/deal modifiers; `mult` is the Reckoning multiplier for that window. */
export function tribute(n: number, mods: Pick<Modifiers, 'tributeMult'>, mult = 1): Decimal {
  let t = tributeRaw(n).mul(mods.tributeMult);
  if (mult !== 1) t = t.mul(mult);
  return t.ceil().max(1);
}

export function spinBudget(n: number, mods: Pick<Modifiers, 'spinsPerWindow'> & Partial<Pick<Modifiers, 'spinsMult'>>, extra = 0): number {
  const base = B.SPINS_BASE + Math.floor((n - 1) / B.SPINS_EVERY) + mods.spinsPerWindow + extra;
  return Math.floor(base * (mods.spinsMult ?? 1));
}

export function par(t: Decimal, spins: number): Decimal {
  return t.div(Math.max(1, spins));
}

/** Souls for paying tribute n: floor(1.33^n) × soulMult (+ flat per tribute). */
export function soulsForTribute(n: number, mods: Pick<Modifiers, 'soulMult' | 'soulFlatPerTribute'> & Partial<Pick<Modifiers, 'tributeSoulX'>>): number {
  return Math.floor(Math.floor(Math.pow(B.SOUL_BASE, n)) * mods.soulMult * (mods.tributeSoulX ?? 1)) + mods.soulFlatPerTribute;
}

/** Bonus when consumed at window n (the one that was failed or abandoned). */
export function consumedBonus(nReached: number, reckoningsBeaten = 0): number {
  return Math.floor(Math.floor(nReached / B.CONSUMED_BONUS_DIV) * (1 + 0.1 * reckoningsBeaten));
}

/** Pay-early bonus: `pct` of par per unused spin, capped at PAY_EARLY_CAP of the tribute. */
export function earlyBonus(t: Decimal, unusedSpins: number, pct: number, spinsTotal: number): Decimal {
  const perSpin = par(t, spinsTotal).mul(pct);
  return perSpin.mul(unusedSpins).min(t.mul(B.PAY_EARLY_CAP)).floor();
}

/** Price helper: a fraction of T(n+1). */
export function priceOf(frac: number, nextTribute: Decimal): Decimal {
  return nextTribute.mul(frac).ceil().max(1);
}
